import React from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const LAYER_TYPES = [
  { type: 'referencePoint', label: 'Reference Points', color: 'bg-blue-500' },
  { type: 'guideLine', label: 'Guide Lines', color: 'bg-purple-500' },
  { type: 'tooth', label: 'Teeth', color: 'bg-amber-500' },
];

const LayersPanel = ({ fabricCanvas, onSaveState }) => {
  if (!fabricCanvas) return null;

  const objects = fabricCanvas.getObjects();

  const getLayerObjects = (type) => objects.filter(o => o.customType === type);
  const getDrawingObjects = () => objects.filter(o => !o.customType);

  const toggleLayerVisibility = (type) => {
    const layerObjs = type === 'drawing' ? getDrawingObjects() : getLayerObjects(type);
    const allVisible = layerObjs.every(o => o.visible !== false);

    layerObjs.forEach(o => {
      o.visible = !allVisible;
    });
    fabricCanvas.renderAll();
  };

  const deleteLayerObjects = (type) => {
    const layerObjs = type === 'drawing' ? getDrawingObjects() : getLayerObjects(type);
    layerObjs.forEach(o => fabricCanvas.remove(o));
    fabricCanvas.renderAll();
    onSaveState();
  };

  const isLayerVisible = (type) => {
    const layerObjs = type === 'drawing' ? getDrawingObjects() : getLayerObjects(type);
    return layerObjs.length > 0 && layerObjs.every(o => o.visible !== false);
  };

  const getLayerCount = (type) => {
    return type === 'drawing' ? getDrawingObjects().length : getLayerObjects(type).length;
  };

  const drawingCount = getDrawingObjects().length;

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 mb-3">Layers</h3>

      <div className="space-y-1.5">
        {LAYER_TYPES.map(layer => {
          const count = getLayerCount(layer.type);
          if (count === 0) return null;

          return (
            <div
              key={layer.type}
              className="flex items-center justify-between p-2 rounded-lg border border-gray-200 bg-white"
            >
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${layer.color}`} />
                <span className="text-xs font-medium text-gray-700">{layer.label}</span>
                <span className="text-[10px] text-gray-400">({count})</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => toggleLayerVisibility(layer.type)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title={isLayerVisible(layer.type) ? 'Hide' : 'Show'}
                >
                  {isLayerVisible(layer.type)
                    ? <EyeIcon className="w-3.5 h-3.5" />
                    : <EyeSlashIcon className="w-3.5 h-3.5" />
                  }
                </button>
                {layer.type !== 'referencePoint' && (
                  <button
                    onClick={() => deleteLayerObjects(layer.type)}
                    className="p-1 text-gray-400 hover:text-red-500 rounded"
                    title="Delete all"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Drawing objects layer */}
        {drawingCount > 0 && (
          <div className="flex items-center justify-between p-2 rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-xs font-medium text-gray-700">Drawings</span>
              <span className="text-[10px] text-gray-400">({drawingCount})</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleLayerVisibility('drawing')}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                title={isLayerVisible('drawing') ? 'Hide' : 'Show'}
              >
                {isLayerVisible('drawing')
                  ? <EyeIcon className="w-3.5 h-3.5" />
                  : <EyeSlashIcon className="w-3.5 h-3.5" />
                }
              </button>
              <button
                onClick={() => deleteLayerObjects('drawing')}
                className="p-1 text-gray-400 hover:text-red-500 rounded"
                title="Delete all drawings"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {objects.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-4">No objects on canvas</p>
        )}
      </div>

      {/* Delete selected */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <button
          onClick={() => {
            const active = fabricCanvas.getActiveObjects();
            if (active.length === 0) return;
            active.forEach(o => fabricCanvas.remove(o));
            fabricCanvas.discardActiveObject();
            fabricCanvas.renderAll();
            onSaveState();
          }}
          className="w-full px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition"
        >
          Delete Selected (Del)
        </button>
      </div>
    </div>
  );
};

export default LayersPanel;
